<template>
  <v-container fill-height>
    <v-row align="center" justify="center" dense>
      <v-col class="py-12">
        <v-card
          elevation="6"
          class="mx-auto"
          variant="outlined"
          color="grey lighten-3"
          rounded="xxl"
          :height="$vuetify.breakpoint.mdAndDown ? 'fit-content' : '600px'"
        >
          <v-card-text>
            <v-row>
              <v-col md="6" sm="6" lg="6" align-self="center">
                <v-img
                  :src="
                    is_company_user
                      ? `${mediaUrl + logo_path}`
                      : require('../../assets/healthy-tantra-logo.png')
                  "
                  alt="SAPP"
                  class="mx-auto"
                  width="200"
                ></v-img>
                <div class="text-center mt-4">
                  <v-chip
                    color="primary"
                    outlined
                    small
                    class="mb-2"
                  >
                    <v-icon small left>mdi-store</v-icon>
                    Seller Portal
                  </v-chip>
                </div>
              </v-col>
              <v-col align-self="center" md="6" sm="6" lg="6" class="py-auto">
                <v-card
                  color="px-4 grey lighten-4 ma-auto"
                  rounded="xl"
                  :height="
                    $vuetify.breakpoint.mdAndDown ? 'fit-content' : '565px'
                  "
                >
                  <v-card-title class="text-h4 mb-1 font-weight-bold">
                    <v-icon large color="primary" class="mr-2">mdi-store</v-icon>
                    Seller Login
                  </v-card-title>
                  <v-card-subtitle class="font-weight-bold">
                    Welcome to the Seller Portal. Please enter your seller credentials.
                  </v-card-subtitle>
                  <v-card-text>
                    <v-text-field
                      v-model="form.email"
                      outlined
                      solo
                      rounded
                      label="Seller Email"
                      placeholder="Enter seller email address"
                      clearable
                      @click:clear="form.email = null"
                      prepend-inner-icon="mdi-email"
                      :error-messages="errors.email"
                    ></v-text-field>
                    <v-text-field
                      v-model="form.password"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="showPassword ? 'text' : 'password'"
                      outlined
                      solo
                      rounded
                      @keypress.enter="login"
                      :error-messages="errors.password"
                      label="Password"
                      placeholder="Enter password"
                      prepend-inner-icon="mdi-lock"
                      @click:append="showPassword = !showPassword"
                    ></v-text-field>
                    <v-btn
                      :loading="isLoading"
                      :disabled="isLoading"
                      color="primary"
                      block
                      rounded
                      @click="login"
                    >
                      <v-icon left>mdi-login</v-icon>
                      Login as Seller
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      align="right"
                      to="/forgot-password"
                    >
                      Forgot password?
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Inactive User Dialog -->
    <v-dialog v-model="showInactiveDialog" max-width="450" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h5 error white--text">
          <v-icon left color="white">mdi-account-off</v-icon>
          Account Inactive
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outlined class="mb-4">
            <div class="text-body-1">
              Your account is currently <strong>inactive</strong>.
            </div>
          </v-alert>
          <p class="text-body-1 mb-2">
            You are unable to access the system at this time. Please contact the administrator for further assistance.
          </p>
          <v-divider class="my-3"></v-divider>
          <p class="text-caption grey--text mb-0">
            <v-icon small class="mr-1">mdi-information</v-icon>
            <strong>Note:</strong> If you believe this is an error, please reach out to the support team with your registered email address.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="showInactiveDialog = false"
            rounded
          >
            <v-icon left>mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Access Restricted Dialog -->
    <v-dialog v-model="showAccessRestrictedDialog" max-width="450" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h5 error white--text">
          <v-icon left color="white">mdi-shield-lock</v-icon>
          Access Restricted
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="error" outlined class="mb-4">
            <div class="text-body-1">
              You do not have permission to access the <strong>Seller Portal</strong>.
            </div>
          </v-alert>
          <p class="text-body-1 mb-2">
            Only users with the <strong>Seller</strong> position can login to the seller portal.
          </p>
          <v-divider class="my-3"></v-divider>
          <p class="text-caption grey--text mb-0">
            <v-icon small class="mr-1">mdi-information</v-icon>
            If you are an admin, please use the <strong>Admin Login</strong> page instead.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-btn
            text
            color="secondary"
            @click="showAccessRestrictedDialog = false; switchToRegularLogin();"
            small
          >
            <v-icon small left>mdi-account</v-icon>
            Go to Admin Login
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="showAccessRestrictedDialog = false"
            rounded
          >
            <v-icon left>mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "@/axios";

export default {
  name: "SellerLoginPage",
  data() {
    return {
      form: {
        email: "",
        password: "",
        portal: true,
        is_seller: true, // Flag to identify seller login
      },
      loggedInRole: "",
      logo_path: "../../assets/healthy-tantra-healthy-tantra-logo.png",
      is_company_user: false,
      showPassword: false,
      isLoading: false,
      showInactiveDialog: false,
      showAccessRestrictedDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      isSeller: "auth/isSeller",
    }),
  },
  mounted() {
    const companyId = localStorage.getItem("company-id");
    if (companyId && companyId.length > 0) {
      this.getCompany(companyId);
      console.log(companyId);
    }
  },
  methods: {
    async getCompany(companyId) {
      await axios
        .get(`/get_company/${this.encryptIt(companyId)}`)
        .then((company) => {
          company = company.data.data;
          this.logo_path = company.logo_path;
        })
        .catch((error) => {
          this.logo_path =
            "../../assets/healthy-tantra-healthy-tantra-logo.png";
          console.error("Error fetching data:", error);
        });
    },
    ...mapActions({
      logIn: "auth/logIn",
      logOut: "auth/logOut",
    }),
    switchToRegularLogin() {
      const currentUrl = window.location;
      let regularUrl;

      if (currentUrl.hostname === 'localhost' || currentUrl.hostname === '127.0.0.1') {
        // For local development, just redirect to regular login
        this.$router.push('/login');
        return;
      } else if (currentUrl.hostname === 'seller.healthytantra.com') {
        regularUrl = 'https://admin.healthytantra.com/login';
      } else if (currentUrl.hostname === 'seller.healthytantra.aaibuzz.com') {
        regularUrl = 'https://healthytantra.aaibuzz.com/login';
      } else {
        // For other environments, remove seller subdomain and add admin
        const hostname = currentUrl.hostname.replace(/^seller\./, '');
        regularUrl = `${currentUrl.protocol}//admin.${hostname}${currentUrl.port ? ':' + currentUrl.port : ''}/login`;
      }

      window.location.href = regularUrl;
    },
    async login() {
      this.isLoading = true;
      try {
        await this.logIn(this.form);
        this.isLoading = false;

        // Only SELLER position can login from seller portal
        if (!this.isSeller) {
          this.logOut();
          this.showAccessRestrictedDialog = true;
          return;
        }

        this.$router.push("/seller-home");
      } catch (error) {
        console.error("Login error:", error);
        this.isLoading = false;
        if (error.isInactiveUser) {
          this.showInactiveDialog = true;
        }
      }
    },
  },
};
</script>